import React, { useEffect, useState } from 'react';
import './VirtualMallLogin.scss';
import { useNavigate, useLocation } from "react-router-dom";
import { virtualMallLoginApi } from './VirtualMallApi/api.js';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const hasRealValue = (value) => {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return text !== "" && text !== "null" && text !== "undefined";
};

export default function VirtualMallLogin() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const redirectUrl = queryParams.get('redirect');
    const hasVirtualSession =
        hasRealValue(localStorage.getItem("virtualmall_token")) &&
        hasRealValue(localStorage.getItem("virtual_user_id"));

    useEffect(() => {
        if (!hasVirtualSession) return;
        navigate(redirectUrl || "/user-points", { replace: true });
    }, [hasVirtualSession, navigate, redirectUrl]);
    const [formData, setFormData] = useState({
        referal_id: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {

        e.preventDefault();

        const newErrors = {};
        if (!formData.referal_id.trim()) {
            newErrors.referal_id = 'Referral ID is required';
        }
        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);

        try {
            // Simulate API call
            const response = await virtualMallLoginApi(formData);
            console.log(response.data.referal_id, "responseLogin", response.data.referal_id?.toString());
            if (response.status === true) {
                if (hasRealValue(response.token)) {
                    localStorage.setItem("virtualmall_token", response.token);
                }
                if (hasRealValue(response.data?.referal_id)) {
                    localStorage.setItem("virtual_referal_id", response.data.referal_id);
                }
                if (hasRealValue(response.data?.user_id)) {
                    localStorage.setItem("virtual_user_id", response.data.user_id);
                }
                if (hasRealValue(response.data?.name)) {
                    localStorage.setItem("virtual_name", response.data.name);
                }
                if (response.data?.single_id_validity) {
                    localStorage.setItem(
                        "virtual_single_id_validity",
                        JSON.stringify(response.data.single_id_validity)
                    );
                }
                toast.success(response.message);

                // Add a small delay to let the toast show before navigating
                setTimeout(() => {
                    if (redirectUrl) {
                        navigate(redirectUrl);
                    } else {
                        navigate('/user-points');
                    }
                }, 1500);

                setFormData({ referal_id: '', password: '' });
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.response?.data?.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = () => {
        navigate('/virtual_mall_forget_password');
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    if (hasVirtualSession) {
        return null;
    }

    return (
        <div className="virtual-mall-login">
            <div className="login-container">
                <div className="login-card">
                    {/* Title */}
                    <h2 className="login-title">Sign In to Virtual Mall</h2>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="login-form">
                        {/* Referral ID Field */}
                        <div className="form-group">
                            <label htmlFor="referal_id" className="form-label">
                                Referral ID
                            </label>
                            <input
                                type="text"
                                id="referal_id"
                                name="referal_id"
                                className={`form-input ${errors.referal_id ? 'error' : ''}`}
                                value={formData.referal_id}
                                onChange={handleChange}
                                placeholder="Enter your referral ID"
                                disabled={loading}
                            />
                            {errors.referal_id && (
                                <div className="error-message">{errors.referal_id}</div>
                            )}
                        </div>

                        {/* Password Field */}
                        <div className="form-group">
                            <label htmlFor="password" className="form-label">
                                Password
                            </label>
                            <div className="password-input">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    name="password"
                                    className={`form-input ${errors.password ? 'error' : ''}`}
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Enter your password"
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={togglePasswordVisibility}
                                    disabled={loading}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? '🙈' : '👁️'}
                                </button>
                            </div>
                            {errors.password && (
                                <div className="error-message">{errors.password}</div>
                            )}
                        </div>

                        {/* Forgot Password Link */}
                        <div className="forgot-password">
                            <button
                                type="button"
                                className="forgot-link"
                                onClick={handleForgotPassword}
                                disabled={loading}
                            >
                                Forgot Password?
                            </button>
                        </div>

                        {/* Login Button */}
                        <button
                            type="submit"
                            className="login-button"
                            disabled={loading || !formData.referal_id || !formData.password}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Signing In...
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
